import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import Layout from "../components/app/Layout";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { FileText, ShieldCheck, ShieldAlert, Download, Landmark, ArrowRight } from "lucide-react";

const STATUS_COLOR = {
    pending: "#FBBF24",
    paid: "#C9A961",
    cancelled: "#71717A",
};

const FILTERS = ["", "pending", "paid", "cancelled"];

export default function AdminFinanceInvoices() {
    const { user, loading } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [status, setStatus] = useState("");
    const [ready, setReady] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const reload = async () => {
        try {
            const [inv, rd] = await Promise.all([
                api.get("/bf/finance/invoices", { params: status ? { status } : {} }),
                api.get("/bf/finance/bank-settings/ready"),
            ]);
            setInvoices(inv.data || []);
            setReady(rd.data);
        } catch (err) { toast.error(formatError(err)); }
    };

    useEffect(() => { if (user?.role === "admin") reload(); }, [user, status]);

    const downloadPdf = async (inv) => {
        setBusyId(inv.id);
        try {
            const res = await api.get(`/bf/finance/invoices/${inv.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = `${inv.invoice_number || inv.id}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) { toast.error(formatError(err)); }
        finally { setBusyId(null); }
    };

    if (loading) return <Layout><div className="px-6 py-32 text-[#71717A]">Loading...</div></Layout>;
    if (!user) return <Navigate to="/login?redirect=/admin/finance/invoices" replace />;
    if (user.role !== "admin") return <Navigate to="/" replace />;

    return (
        <Layout>
            <div className="px-6 md:px-12 lg:px-16 pt-10 pb-32">
                <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-[#C9A961]" />
                    <p className="overline">Business Finance · Invoices</p>
                </div>
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mt-3">
                    <div>
                        <h1 className="font-display text-5xl md:text-6xl">Invoices</h1>
                        <p className="text-sm text-[#A1A1AA] mt-3 max-w-2xl">
                            Every generated PDF pulls bank data from your Bank Configuration. Keep it complete
                            to make sure no invoice goes out with a blank IBAN.
                        </p>
                    </div>
                    <BankReadyBanner ready={ready} />
                </div>

                <div className="flex gap-2 mt-10 items-center" data-testid="invoices-filter">
                    <span className="overline">Status:</span>
                    {FILTERS.map((s) => (
                        <button key={s || "all"} onClick={() => setStatus(s)}
                            className={`text-xs uppercase tracking-wide px-3 py-1.5 border ${status === s ? "border-[#C9A961] text-[#C9A961]" : "border-[#27272A] text-[#A1A1AA] hover:text-white"}`}
                            data-testid={`invoices-filter-${s || "all"}`}>
                            {s || "all"}
                        </button>
                    ))}
                </div>

                <div className="mt-8">
                    {invoices.length === 0 ? (
                        <div className="border border-[#27272A] p-16 text-center text-[#A1A1AA]">
                            No invoices yet.
                        </div>
                    ) : (
                        <div className="border border-[#27272A] overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-[#0A0A0A]">
                                    <tr className="text-left text-[#71717A] uppercase tracking-wide text-xs">
                                        <th className="py-3 px-4">Number</th>
                                        <th className="py-3 px-4">Type</th>
                                        <th className="py-3 px-4">Winery</th>
                                        <th className="py-3 px-4">Amount</th>
                                        <th className="py-3 px-4">Status</th>
                                        <th className="py-3 px-4">Issued</th>
                                        <th className="py-3 px-4"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoices.map((inv) => (
                                        <tr key={inv.id} className="border-t border-[#27272A]" data-testid={`invoice-row-${inv.id}`}>
                                            <td className="py-3 px-4 font-mono text-xs text-[#E4E4E7]">{inv.invoice_number}</td>
                                            <td className="py-3 px-4 capitalize">{inv.invoice_type}</td>
                                            <td className="py-3 px-4 text-[#E4E4E7]">{inv.winery_name}</td>
                                            <td className="py-3 px-4 text-[#C9A961]">
                                                {inv.currency} {Number(inv.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </td>
                                            <td className="py-3 px-4">
                                                <span className="text-xs uppercase tracking-wide" style={{ color: STATUS_COLOR[inv.status] || "#A1A1AA" }}>
                                                    {inv.status}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-xs text-[#71717A] whitespace-nowrap">{(inv.issued_at || "").slice(0, 10)}</td>
                                            <td className="py-3 px-4">
                                                <button onClick={() => downloadPdf(inv)} disabled={busyId === inv.id}
                                                    className="btn-outline !py-1.5 !px-3 !text-[10px] tracking-widest"
                                                    data-testid={`invoice-pdf-${inv.id}`}
                                                    title={ready?.bank_ready ? "Download invoice PDF" : "Warning: bank config incomplete — PDF will be missing bank data"}
                                                >
                                                    <Download className="w-3 h-3" /> {busyId === inv.id ? "..." : "PDF"}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

function BankReadyBanner({ ready }) {
    if (!ready) return null;
    const ok = !!ready.bank_ready;
    if (ok) {
        return (
            <div className="flex items-start gap-3 border border-[#C9A961] px-4 py-3 max-w-sm" data-testid="invoices-bank-ready">
                <ShieldCheck className="w-4 h-4 text-[#C9A961] mt-0.5" />
                <div>
                    <p className="text-xs uppercase tracking-widest text-[#C9A961]">Bank Ready</p>
                    <p className="text-[11px] text-[#A1A1AA] mt-1">
                        Bank configuration complete. New PDFs will include full IBAN + SWIFT details.
                    </p>
                    <Link to="/admin/finance/bank" className="text-[10px] uppercase tracking-widest text-[#71717A] hover:text-white inline-flex items-center gap-1 mt-2">
                        <Landmark className="w-3 h-3" /> Manage <ArrowRight className="w-3 h-3" />
                    </Link>
                </div>
            </div>
        );
    }
    return (
        <div className="flex items-start gap-3 border border-[#F97316] px-4 py-3 max-w-sm" data-testid="invoices-bank-incomplete">
            <ShieldAlert className="w-4 h-4 text-[#F97316] mt-0.5" />
            <div>
                <p className="text-xs uppercase tracking-widest text-[#F97316]">Bank Incomplete</p>
                <p className="text-[11px] text-[#A1A1AA] mt-1">
                    {ready.missing_fields?.length
                        ? <>Missing: <span className="text-[#E4E4E7]">{ready.missing_fields.join(", ")}</span>. New PDFs will ship without those fields.</>
                        : "Bank details missing. New PDFs may go out with blank IBAN."}
                </p>
                <Link to="/admin/finance/bank" className="text-[10px] uppercase tracking-widest text-[#F97316] hover:text-white inline-flex items-center gap-1 mt-2">
                    <Landmark className="w-3 h-3" /> Configure now <ArrowRight className="w-3 h-3" />
                </Link>
            </div>
        </div>
    );
}
